/**
 * Shared rendering helpers for note / message threads (Resident and Reimbursement
 * "Notes & Messages" blocks render the same Angular-authored HTML and timestamps).
 */

import { format } from 'date-fns'

/** Longest a numeric string can be and still be read as a unix timestamp rather than a date. */
const MAX_EPOCH_STRING_LENGTH = 11

/** Angular `date:'yyyy-MM-dd hh:mm:ss a'` for notes/messages lists. */
const ANGULAR_NOTE_TIMESTAMP = 'yyyy-MM-dd hh:mm:ss a'

/** Already Angular-display shaped: `2026-08-03 02:00:44 AM` (do not re-parse via Date). */
const ANGULAR_DISPLAY_TS =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2}):(\d{2})\s*([AaPp][Mm])$/

/**
 * Backend `formatDate('Y-m-d H:i:s')` uses `config('app.timezone')` (UTC).
 * Laravel/MySQL often appends fractional seconds (`.000000`) with no `Z`.
 * Those must be read as UTC — `Date.parse` treats naive strings as local and
 * flips the calendar day for IST (save shows day 3, reload shows day 2).
 */
const MYSQL_UTC_TS =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i

/**
 * Strips scripts, inline event handlers and `javascript:` URLs from note bodies before
 * they are rendered with `dangerouslySetInnerHTML`. Not a general-purpose sanitizer —
 * it covers the Angular-authored note HTML this app stores.
 */
export function sanitizeNoteHtml(html: string): string {
  return html
    .replace(/<\/?(script|style)[^>]*>/gi, '')
    .replace(/ on[a-z]+="[^"]*"/gi, '')
    .replace(/ on[a-z]+='[^']*'/gi, '')
    .replace(/javascript:/gi, '')
}

function fromLocalParts(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  s: number,
): Date | null {
  const date = new Date(y, mo - 1, d, h, mi, s)
  return Number.isNaN(date.getTime()) ? null : date
}

function fromUtcParts(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  s: number,
): Date {
  return new Date(Date.UTC(y, mo - 1, d, h, mi, s))
}

function toNoteDate(value: unknown): Date | null {
  if (value === undefined || value === null || value === '') return null

  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isNaN(numeric) && String(value).trim().length <= MAX_EPOCH_STRING_LENGTH) {
    return new Date(numeric > 1e12 ? numeric : numeric * 1000)
  }

  const raw = String(value).trim()

  const display = ANGULAR_DISPLAY_TS.exec(raw)
  if (display) {
    let h = Number(display[4])
    const ampm = display[7].toUpperCase()
    if (ampm === 'PM' && h < 12) h += 12
    if (ampm === 'AM' && h === 12) h = 0
    return fromLocalParts(
      Number(display[1]),
      Number(display[2]),
      Number(display[3]),
      h,
      Number(display[5]),
      Number(display[6]),
    )
  }

  const mysql = MYSQL_UTC_TS.exec(raw)
  if (mysql) {
    // Explicit Z / offset: trust Date.parse. Naive / fractional: UTC (app timezone).
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
      const parsed = Date.parse(raw)
      if (!Number.isNaN(parsed)) return new Date(parsed)
    }
    return fromUtcParts(
      Number(mysql[1]),
      Number(mysql[2]),
      Number(mysql[3]),
      Number(mysql[4]),
      Number(mysql[5]),
      Number(mysql[6]),
    )
  }

  const parsed = Date.parse(raw)
  if (Number.isNaN(parsed)) return null
  return new Date(parsed)
}

/** Formats a note timestamp, accepting unix seconds/millis or any parseable date string. */
export function formatNoteWhen(value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  const raw = String(value).trim()
  // Keep an already-correct Angular display string stable (avoids save→reload flicker).
  if (ANGULAR_DISPLAY_TS.test(raw)) {
    const date = toNoteDate(raw)
    return date ? format(date, ANGULAR_NOTE_TIMESTAMP) : raw
  }
  const date = toNoteDate(value)
  if (!date || Number.isNaN(date.getTime())) return raw
  return format(date, ANGULAR_NOTE_TIMESTAMP)
}

/**
 * Resolves the author label for a public/internal note or message row.
 * Angular templates bind `messagerow.name`; API payloads also use `sender_name`,
 * `user_name`, `created_by_name`, or `first_name`/`last_name` depending on source.
 */
export function resolveNoteAuthorName(row: Record<string, unknown> | null | undefined): string {
  if (!row) return '—'

  const candidates = [row.name, row.sender_name, row.user_name, row.created_by_name, row.created_by]
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue
    const text = String(candidate).trim()
    // Skip bare numeric ids (fk_user_id-style values sometimes land in created_by).
    if (!text || /^\d+$/.test(text)) continue
    return text
  }

  const first = String(row.first_name ?? '').trim()
  const last = String(row.last_name ?? '').trim()
  const combined = `${first} ${last}`.trim()
  if (combined) return combined

  return '—'
}
